import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Map from 'ol/Map';
import { useElement } from './useElement';
import { useLayers } from './layerManagement/LayerContext';
import {
  extractTargetInfos,
  getTargetInfoByMMSI,
  InvalidMMSIError,
  TargetTypeEnum,
  useSession,
} from '@ais-ui/websockets';
import { useSelectionState } from '@ais-ui/custom-hooks';
import { useViewMode, ViewMode } from '@ais-ui/custom-hooks';
import { createOverlayImageLoader } from './overlayImageLoader';
import { SingleFrameImageWMS } from './singleFrameImageWMS';

interface ImageWMSLayerProps {
  map: Map;
  overlay: string | null;
  hasViewModeChanged: boolean;
  setHasViewModeChanged: React.Dispatch<React.SetStateAction<boolean>>;
  name: string;
}

const useUpdateTargetDetails = () => {
  const { viewMode } = useViewMode();
  const { olSession, replaySession } = useSession();
  const { targetInfoDetailsList, setTargetInfoDetailsList } =
    useSelectionState();

  const updateSelectedTargets = useCallback(async () => {
    if (targetInfoDetailsList.length === 0) return;

    const updatedList = [...targetInfoDetailsList];
    let hasChanges = false;

    for (let index = 0; index < targetInfoDetailsList.length; index++) {
      const targetDetails = targetInfoDetailsList[index];

      if (targetDetails.extractedTargetInfo.length === 0) continue;

      const target = targetDetails.extractedTargetInfo[0];
      const mmsiRefreshable = new Set([
        TargetTypeEnum.Ship,
        TargetTypeEnum.Aton,
        TargetTypeEnum.VirtualAton,
        TargetTypeEnum.BaseStation,
      ]);
      if (
        'mmsi' in target &&
        mmsiRefreshable.has(targetDetails.extractedTargetInfoType)
      ) {
        const session =
          viewMode === ViewMode.Replay ? replaySession : olSession;

        if (!session) {
          continue;
        }

        try {
          const targetInfo = await getTargetInfoByMMSI(
            session,
            Number(target.mmsi),
          );

          const extractedTargetInfo = extractTargetInfos(targetInfo);

          // Same MMSI can represent different target types (e.g., AtoN +
          // Environmental on the same physical buoy). Only accept the
          // refresh if the type still matches the original selection.
          if (
            extractedTargetInfo.extractedTargetInfoType !==
            targetDetails.extractedTargetInfoType
          ) {
            continue;
          }

          updatedList[index] = extractedTargetInfo;
          hasChanges = true;
        } catch (error) {
          if (error instanceof InvalidMMSIError) {
            console.warn(`Invalid MMSI for target at index ${index}:`, error);
            // Keep the existing target info if MMSI is invalid
            continue;
          }
          throw error;
        }
      }
    }

    if (!hasChanges) return;

    // Use functional update to prevent overwriting a newer selection.
    // If something else (e.g., a map click) changed the selection while
    // our async MMSI fetches were in-flight, skip this stale update.
    setTargetInfoDetailsList((currentList) => {
      if (currentList !== targetInfoDetailsList) {
        return currentList;
      }
      return updatedList;
    });
  }, [
    targetInfoDetailsList,
    setTargetInfoDetailsList,
    viewMode,
    olSession,
    replaySession,
  ]);

  return updateSelectedTargets;
};

function useLatestTargetRefresh(refresh: () => Promise<void>) {
  const latestRefresh = useRef(refresh);
  useLayoutEffect(() => {
    latestRefresh.current = refresh;
  }, [refresh]);

  return useCallback(() => {
    void latestRefresh.current().catch((error) => {
      console.error('Failed to refresh selected map target details:', error);
    });
  }, []);
}

const ignoreImageLoad = () => {};

export function ImageWMSLayer({
  map,
  overlay,
  hasViewModeChanged,
  setHasViewModeChanged,
  name,
}: ImageWMSLayerProps) {
  const [layer, setLayer] = useState(() => new ImageLayer<ImageWMS>({}));
  const { canvasRef } = useLayers();
  const refreshSelectedTargets = useLatestTargetRefresh(
    useUpdateTargetDetails(),
  );
  const loaderRef = useRef<ReturnType<typeof createOverlayImageLoader> | null>(
    null,
  );
  const sourceRef = useRef<SingleFrameImageWMS | null>(null);

  useEffect(() => {
    // Each effect lifetime gets a fresh source/loader. StrictMode can reuse
    // the state-held layer after cleanup, but never a disposed source.
    const source = new SingleFrameImageWMS({ url: '', ratio: 1 });
    const loader = createOverlayImageLoader(canvasRef, refreshSelectedTargets);
    loaderRef.current = loader;
    sourceRef.current = source;
    source.setImageLoadFunction(loader.load);
    layer.setSource(source);
    return () => {
      loader.dispose();
      if (loaderRef.current === loader) loaderRef.current = null;
      if (sourceRef.current === source) sourceRef.current = null;
      if (layer.getSource() === source) layer.setSource(null);
      source.setImageLoadFunction(ignoreImageLoad);
      source.dispose();
    };
  }, [layer, canvasRef, refreshSelectedTargets]);

  useEffect(() => {
    // Only a genuinely new overlay may trigger a fresh fetch; panning/zooming
    // must keep rendering the pinned image without touching the loader.
    if (loaderRef.current?.setOverlay(overlay)) {
      sourceRef.current?.requestFreshImage();
    }
    layer.getSource()?.changed();
  }, [layer, overlay, map, canvasRef, refreshSelectedTargets]);

  useElement(layer, name);

  useEffect(() => {
    if (!hasViewModeChanged) return;
    setLayer(new ImageLayer<ImageWMS>({}));
    setHasViewModeChanged(false);
  }, [hasViewModeChanged, setHasViewModeChanged]);

  return null;
}
